// Project case studies.
// `writeup` is the hand-written text at the top of each project page; separate paragraphs with a blank line.
// `sections` builds the rest of the page, in order. Each section has a `type` that picks its layout
// (see the section components in src/pages/ProjectPage.js):
//   contrast — two text columns, e.g. before/after
//   feature  — one image beside text; `side` puts the image 'left' or 'right'
//   wide     — heading and text above a full-width image; `narrow` keeps small images from blowing up
//   pair     — two images side by side; `arrow` draws a "this leads to that" arrow between them
//   gallery  — a grid of images; an item with `span` takes the full row
//   stats    — a row of headline numbers
//   compare  — input and output videos side by side
//   note     — a single short callout line
// `demo` (a YouTube id) and `publications` always come last on the page.

import tmHome from '../assets/trackmate/home.png';
import tmFirstFrame from '../assets/trackmate/Tracked_first_frame.png';
import tmDownload from '../assets/trackmate/processed_video_download.png';
import tmExampleInput from '../assets/trackmate/example-input.mp4';
import tmExampleOutput from '../assets/trackmate/example-output.mp4';

import bcSignIn from '../assets/businesscompanion/signinPage.png';
import bcActivity from '../assets/businesscompanion/activityDashboard.png';
import bcClients from '../assets/businesscompanion/clientDashboard.png';
import bcAddClient from '../assets/businesscompanion/addClient.png';
import bcQr from '../assets/businesscompanion/clientQR.png';
import bcForm from '../assets/businesscompanion/employeeForm.png';

import ulJointPrediction from '../assets/upperlimb/jointPrediction.png';
import ulEmgImu from '../assets/upperlimb/EmgImu.png';
import ulPlacement from '../assets/upperlimb/EmgImuPlacement.png';
import ulProtocol from '../assets/upperlimb/dataCollectionProto.png';
import ulVicon from '../assets/upperlimb/ViconMotionCapture.png';
import ulOpenSim from '../assets/upperlimb/OpensimProcessing.png';
import ulTeacherStudent from '../assets/upperlimb/teacherStudent.gif';
import ulVisualization from '../assets/upperlimb/visualization.png';
import ulPipeline from '../assets/upperlimb/ProjectPipeline.png';

export const projects = [
    {
        slug: 'businesscompanion',
        title: 'BusinessCompanion',
        subtitle: 'Billing Workflow Automation',
        stack: ['React', 'Node.js', 'Express', 'PostgreSQL', 'Google Sheets API', 'OAuth'],
        summary:
            'A full-stack web app that replaced HouseMedix’s paper-based billing workflow with digital submissions, client management and live reporting.',
        cover: bcActivity,
        coverAlt: 'BusinessCompanion activity dashboard showing the shared Google Sheets activity log',
        hero: bcSignIn,
        heroAlt: 'BusinessCompanion sign-in page',
        github: 'https://github.com/stevenc15/businessCompanion',
        writeup: `My friend's girlfriend's dad was spending hours each weekend manually billing his clients on a paper-heavy system. I digitized his billing process, leaving him only with the task of sending out the invoices.`,
        sections: [
            {
                type: 'contrast',
                title: 'From paper to a spreadsheet that fills itself',
                columns: [
                    {
                        label: 'Before',
                        points: [
                            'Client visits were recorded on paper.',
                            'Weekends went to turning those records into bills by hand.',
                        ],
                    },
                    {
                        label: 'After',
                        points: [
                            'Employees log each visit from their phone at the client’s door.',
                            'Every entry lands in his Google Sheet automatically.',
                            'All that’s left for him is sending the invoices.',
                        ],
                        accent: true,
                    },
                ],
            },
            {
                type: 'pair',
                arrow: true,
                title: 'Scan the door, and the form fills itself',
                body: 'Each client gets a QR code that the employer prints and leaves at the residence. When an employee scans it, an activity form opens with that client’s details already filled in, so the only thing left to enter is the visit itself.',
                items: [
                    {
                        image: bcQr,
                        alt: 'Client dashboard with a generated QR code and a Download QR Code PDF button',
                        caption: 'A printable QR code for each client',
                    },
                    {
                        image: bcForm,
                        alt: 'Mobile activity logging form with client details pre-filled',
                        caption: 'Scanning it opens a pre-filled form',
                        portrait: true,
                    },
                ],
            },
            {
                type: 'wide',
                title: 'Every visit lands in one Google Sheet',
                body: 'Submitted forms are written straight to a centralized Google Sheets file, shown live on the activity dashboard. The file stays in the employer’s own Google account, so he keeps full access to and control of his records, and billing starts from a complete log instead of a stack of paper.',
                image: bcActivity,
                alt: 'Activity dashboard with an embedded Google Sheets activity log',
            },
            {
                type: 'pair',
                title: 'Client records in one place',
                body: 'The client dashboard lists every client, with search and controls to edit or remove records. New clients are added from a simple form, and each one gets its QR code right away.',
                items: [
                    {
                        image: bcClients,
                        alt: 'Client dashboard listing client records',
                        caption: 'Search, edit and manage every client',
                    },
                    {
                        image: bcAddClient,
                        alt: 'Form for adding a new client',
                        caption: 'Add a client in one form',
                    },
                ],
            },
            {
                type: 'note',
                text: 'Access is locked down with Google OAuth: only authorized Gmail accounts can sign in.',
            },
        ],
    },
    {
        slug: 'trackmate',
        title: 'TrackMate',
        subtitle: 'Soccer Tracker App',
        stack: ['YOLOv5', 'OpenCV', 'Python', 'NumPy', 'scikit-learn', 'Node.js', 'Express', 'MySQL'],
        summary:
            'A full-stack ML app that tracks one chosen player through soccer footage, so athletes can build highlight reels without hours of manual editing.',
        cover: tmFirstFrame,
        coverAlt: 'Soccer match frame with a bounding box and ID drawn around every player',
        hero: tmHome,
        heroAlt: 'TrackMate landing page',
        github: 'https://github.com/stevenc15/TrackMate',
        writeup: `My brother needed a way to edit his soccer highlights without being stuck to the computer for hours, so I made TrackMate to speed up and automate the process.`,
        sections: [
            {
                type: 'feature',
                side: 'left',
                eyebrow: 'Pick your player',
                title: 'Every player gets an ID on the first frame',
                body: 'Upload a clip of about 30 seconds and TrackMate detects every player in the opening frame, drawing a bounding box and an ID around each one. Instead of scrubbing through footage, you just type in the ID of the player you want to follow.',
                image: tmFirstFrame,
                alt: 'First frame with player IDs and a field to enter the target player ID',
            },
            {
                type: 'compare',
                eyebrow: 'Locked on',
                title: 'One player, followed through the whole clip',
                body: 'From that single ID, TrackMate keeps the chosen player annotated from the first frame to the last. The original footage is on the left and TrackMate’s output on the right.',
                input: tmExampleInput,
                output: tmExampleOutput,
            },
            {
                type: 'feature',
                side: 'right',
                eyebrow: 'Highlight-ready',
                title: 'A finished clip, not a project file',
                body: 'The processed video comes back ready to download, with your player marked throughout, so it can go straight into a highlight reel with no manual editing.',
                image: tmDownload,
                alt: 'Processed video with a download button',
            },
        ],
        demo: { youtubeId: 'PibIhiw_kro', title: 'TrackMate demo' },
    },
    {
        slug: 'upper-limb',
        title: 'Upper-Limb Joint Prediction',
        subtitle: 'Wearable sensor-based pose estimation',
        stack: ['Python', 'PyTorch', 'Pandas', 'NumPy', 'OpenSim', 'Vicon Nexus'],
        summary:
            'A deep-learning pipeline that predicts upper-limb joint angles from IMU and EMG sensors, working toward less invasive, wearable-based motion tracking.',
        cover: ulVisualization,
        coverAlt: 'EMG and IMU signals plotted beside a visualization of predicted arm joint angles',
        hero: ulPipeline,
        heroAlt: 'Pipeline diagram from Vicon capture and OpenSim processing to a PyTorch model that outputs joint angles',
        github: 'https://github.com/stevenc15/upper-limb-joint-prediction',
        writeup: `Normally tracking upper-limb joint angles is an expensive and invasive process. This project aims to simplify the process by leveraging deep learning to predict upper-limb joint angles from just one IMU sensor on the wrist instead of using a full motion capture system.`,
        sections: [
            {
                type: 'feature',
                side: 'right',
                eyebrow: 'Background',
                title: 'Pose estimation without the lab',
                body: 'Pose estimation tracks the posture of the body from image, video or sensor input. The accurate version, a marker-based motion capture lab, is expensive and impractical outside research settings. The goal here is to get joint angles from wearable sensors alone.',
                image: ulJointPrediction,
                alt: 'Diagram of joint positions on an arm',
            },
            {
                type: 'stats',
                title: 'The dataset',
                items: [
                    { value: '10', label: 'subjects' },
                    { value: '5', label: 'movement categories' },
                    { value: '6', label: 'EMG / IMU sensors' },
                    { value: '~60', label: 'motion capture markers' },
                ],
            },
            {
                type: 'gallery',
                eyebrow: 'Inputs',
                title: 'Wearable sensing',
                body: 'Six wearable sensors record inertial (IMU) and muscle-activity (EMG) signals. Three are placed for EMG on the biceps brachii, triceps brachii and posterior deltoid, and three for IMU around the lower arm, with a possible product extension in mind. A five-category protocol of swings, reaches and elbow movements covers the subject’s full range of motion.',
                items: [
                    { image: ulEmgImu, alt: 'IMU and EMG sensors', caption: 'IMU and EMG sensors' },
                    {
                        image: ulPlacement,
                        alt: 'Placement of six EMG and IMU sensors on a subject’s arm',
                        caption: 'Sensor placement',
                    },
                    {
                        image: ulProtocol,
                        alt: 'Data collection protocol table covering arm swing, overhead reach, elbow flexion, elbow rotation and cross-body reach trials',
                        caption: 'Data collection protocol',
                        span: true,
                    },
                ],
            },
            {
                type: 'pair',
                arrow: true,
                eyebrow: 'Labels',
                title: 'Ground truth from motion capture',
                body: 'While the sensors record, a Vicon system captures about 60 markers in Vicon Nexus. The raw marker trajectories are then refined in OpenSim to recover accurate joint angles, which become the labels the model learns to predict.',
                items: [
                    {
                        image: ulVicon,
                        alt: 'Subject wearing motion capture markers in a Vicon lab',
                        caption: 'Vicon motion capture',
                    },
                    { image: ulOpenSim, alt: 'OpenSim musculoskeletal model', caption: 'OpenSim joint angles' },
                ],
            },
            {
                type: 'wide',
                narrow: true,
                eyebrow: 'Results',
                title: 'Model performance',
                body: 'Predictions from a teacher–student knowledge-distillation setup, compared against ground truth. Architecture details will be shared once published, since the original model belongs to a UCF graduate student on the project.',
                image: ulTeacherStudent,
                alt: 'Animated comparison of ground truth, teacher, student and distilled student predictions',
                caption: 'Left to right: ground truth, teacher, student, and student with knowledge distillation',
            },
            {
                type: 'feature',
                side: 'left',
                title: 'Signals and predictions, side by side',
                body: 'A visualization tool plots the incoming EMG and IMU signals next to the predicted joint angles, making it easy to see how the model responds to each movement.',
                image: ulVisualization,
                alt: 'EMG and IMU signal plots next to predicted joint angles',
            },
        ],
        publications: [
            {
                status: 'Published',
                title: 'Creation and Utilization of an Upper Limb Movement Dataset for Machine Learning Applications',
                description:
                    'Co-author. A dataset of five upper-limb movements from each of ten subjects. I contributed to data collection, preprocessing with Pandas and NumPy, and quality and consistency checks for model training and validation.',
                link: 'https://www.techrxiv.org/doi/full/10.36227/techrxiv.174650748.85825277',
                venue: 'TechRxiv',
            },
            {
                status: 'In progress',
                title: 'Upper-Limb Joint Prediction Using IMUs: A Deep Learning Approach for Kinematics Estimation',
                description:
                    'Co-author. Predicting upper-limb joint movement from IMU and EMG data with deep learning, covering data processing, a PyTorch model and pipeline, and a comparison with a domain-adaptation model.',
            },
        ],
    },
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);
